import React, { useState } from 'react';
import type { LayerRenderer } from '../../../types';
import {
  POINT_SYMBOLS,
  SYMBOL_CATEGORIES,
  getPointSymbol,
  type SymbolCategory,
} from '../../../layers/symbols';
import { ColorField, PropertySection, SliderField } from './SymbolProperties';

/**
 * Point symbology: a categorised symbol gallery plus symbol properties.
 * Every change is emitted immediately so the map previews live.
 */
export default function PointSymbolGallery({
  renderer,
  onChange,
  /** WMS is rendered by GeoServer, which only understands SLD marks. */
  sldOnly = false,
}: {
  renderer: LayerRenderer;
  onChange: (changes: Partial<LayerRenderer>) => void;
  sldOnly?: boolean;
}) {
  const [category, setCategory] = useState<SymbolCategory | 'All'>('All');
  const selected = getPointSymbol(renderer.symbolId);

  const available = sldOnly ? POINT_SYMBOLS.filter((symbol) => symbol.sldMark) : POINT_SYMBOLS;
  const visible =
    category === 'All'
      ? available
      : available.filter((symbol) => symbol.category === category);

  return (
    <>
      <PropertySection title="Symbol">
        {sldOnly && (
          <p className="mb-2 rounded bg-amber-50 px-2 py-1.5 text-[10px] leading-relaxed text-amber-800">
            This layer is drawn by GeoServer, which supports a standard set of SLD marks.
            Only those symbols are shown.
          </p>
        )}

        {/* Category filter */}
        <div className="mb-2 flex flex-wrap gap-1">
          {(['All', ...SYMBOL_CATEGORIES] as const).map((entry) => (
            <button
              key={entry}
              type="button"
              onClick={() => setCategory(entry as SymbolCategory | 'All')}
              className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition ${
                category === entry
                  ? 'bg-[#0F2942] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {entry}
            </button>
          ))}
        </div>

        <div className="grid max-h-56 grid-cols-4 gap-1.5 overflow-y-auto rounded-lg border border-slate-200 p-2">
          {visible.map((symbol) => {
            const isSelected = symbol.id === selected.id;
            return (
              <button
                key={symbol.id}
                type="button"
                title={`${symbol.name} · ${symbol.category}`}
                aria-pressed={isSelected}
                onClick={() => onChange({ symbolId: symbol.id, sldMark: symbol.sldMark })}
                className={`flex flex-col items-center gap-1 rounded-lg border px-1 py-1.5 transition ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-transparent hover:border-slate-200 hover:bg-slate-50'
                }`}
              >
                <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
                  <path
                    d={symbol.path}
                    fill={renderer.color}
                    stroke={renderer.outlineColor}
                    strokeWidth={0.75}
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="w-full truncate text-center text-[9px] leading-tight text-slate-500">
                  {symbol.name}
                </span>
              </button>
            );
          })}
        </div>
        <p className="mt-1.5 text-[11px] text-slate-400">
          Selected: <span className="font-medium text-slate-600">{selected.name}</span>
        </p>
      </PropertySection>

      <PropertySection title="Appearance">
        {renderer.type === 'single' && (
          <ColorField label="Colour" value={renderer.color} onChange={(color) => onChange({ color })} />
        )}
        <SliderField
          label="Size"
          value={renderer.pointSize}
          min={6}
          max={48}
          suffix="px"
          onChange={(pointSize) => onChange({ pointSize })}
        />
        <SliderField
          label="Opacity"
          value={Math.round(renderer.fillOpacity * 100)}
          min={0}
          max={100}
          suffix="%"
          onChange={(value) => onChange({ fillOpacity: value / 100 })}
        />
        <SliderField
          label="Rotation"
          value={renderer.rotation ?? 0}
          min={0}
          max={360}
          suffix="°"
          onChange={(rotation) => onChange({ rotation })}
        />
      </PropertySection>

      <PropertySection title="Outline">
        <ColorField
          label="Colour"
          value={renderer.outlineColor}
          onChange={(outlineColor) => onChange({ outlineColor })}
        />
        <SliderField
          label="Width"
          value={renderer.outlineWidth}
          min={0}
          max={6}
          step={0.5}
          suffix="px"
          onChange={(outlineWidth) => onChange({ outlineWidth })}
        />
      </PropertySection>
    </>
  );
}
